import {
  type Category,
  DescriptorType,
  type Domain,
  type FCategories,
  type FTags,
  type Tag,
  type UniqueIdentifier,
} from "@piperunner-cms/types/v1";
import { and, arrayUnion, collection, doc, getDocs, limit, query, setDoc, updateDoc, where } from "firebase/firestore";
import { fsDb } from "@/client/utils/initialize-fs-db";
import { FIRESTORE_COLLECTION_PATH, NotFoundError } from "@/constants";
import { isUniqueIdentifierPresent } from "@/utils";

export const createCategory = async (
  identifier: UniqueIdentifier,
  category: Category,
  domainId: Domain["id"],
): Promise<Category> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS);

  const docQuery = query(
    collectionRef,
    and(
      where("userId", "==", userId),
      where("projectId", "==", projectId),
      where("domainId", "==", domainId),
      where("type", "==", DescriptorType.CATEGORY),
    ),
    limit(1),
  );
  const docSnap = await getDocs(docQuery);

  const categoryMetadata: Omit<FCategories, "categories" | "id"> = {
    userId,
    projectId,
    type: DescriptorType.CATEGORY,
    domainId,
  };

  if (docSnap.empty) {
    const docRef = doc(collectionRef);
    await setDoc(docRef, {
      ...categoryMetadata,
      id: docRef.id,
      categories: arrayUnion(category),
    });
  } else {
    const existingDoc = docSnap.docs[0];
    await updateDoc(doc(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS, existingDoc.id), {
      ...categoryMetadata,
      id: existingDoc.id,
      categories: arrayUnion(category),
    });
    return category;
  }
};

export const getCategories = async (identifier: UniqueIdentifier, domainId: Domain["id"]): Promise<FCategories> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS);
  const docQuery = query(
    collectionRef,
    and(
      where("userId", "==", userId),
      where("projectId", "==", projectId),
      where("domainId", "==", domainId),
      where("type", "==", DescriptorType.CATEGORY),
    ),
    limit(1),
  );
  const docSnap = await getDocs(docQuery);

  if (docSnap.empty) {
    throw new NotFoundError(
      `No categories found for userId: ${userId}, projectId: ${projectId} and domainId: ${domainId}`,
    );
  }

  const categories = {
    userId: docSnap.docs[0].data().userId,
    projectId: docSnap.docs[0].data().projectId,
    id: docSnap.docs[0].data().id,
    type: DescriptorType.CATEGORY,
    domainId: docSnap.docs[0].data().domainId,
    categories: docSnap.docs[0].data().categories || [],
  } as FCategories;

  return categories;
};

export const updateCategory = async (
  identifier: UniqueIdentifier,
  updatedCategory: Category,
  domainId: Domain["id"],
): Promise<Category> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS);
  const docQuery = query(
    collectionRef,
    and(
      where("userId", "==", userId),
      where("projectId", "==", projectId),
      where("domainId", "==", domainId),
      where("type", "==", DescriptorType.CATEGORY),
    ),
    limit(1),
  );
  const docSnap = await getDocs(docQuery);
  if (docSnap.empty) {
    throw new NotFoundError(
      `No categories found for userId: ${userId}, projectId: ${projectId} and domainId: ${domainId}`,
    );
  }
  const existingDoc = docSnap.docs[0];
  const existingCategories: Category[] = existingDoc.data().categories || [];

  const doesIdExist = existingCategories.some((cat) => cat.id === updatedCategory.id);
  if (!doesIdExist) {
    throw new NotFoundError(`Category with id: ${updatedCategory.id} does not exist.`);
  }

  const updatedCategories = existingCategories.map((cat) => (cat.id === updatedCategory.id ? updatedCategory : cat));
  await updateDoc(doc(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS, existingDoc.id), {
    categories: updatedCategories,
  });
  return updatedCategory;
};

export const deleteCategory = async (
  identifier: UniqueIdentifier,
  categoryId: Category["id"],
  domainId: Domain["id"],
): Promise<void> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS);

  const docQuery = query(
    collectionRef,
    and(
      where("userId", "==", userId),
      where("projectId", "==", projectId),
      where("domainId", "==", domainId),
      where("type", "==", DescriptorType.CATEGORY),
    ),
    limit(1),
  );
  const docSnap = await getDocs(docQuery);
  if (docSnap.empty) {
    throw new NotFoundError(
      `No categories found for userId: ${userId}, projectId: ${projectId} and domainId: ${domainId}`,
    );
  }
  const existingDoc = docSnap.docs[0];
  const existingCategories: Category[] = existingDoc.data().categories || [];
  const updatedCategories = existingCategories.filter((cat) => cat.id !== categoryId);
  await updateDoc(doc(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS, existingDoc.id), {
    categories: updatedCategories,
  });
};

export const getTags = async (identifier: UniqueIdentifier, domainId: Domain["id"]): Promise<FTags> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS);
  const docQuery = query(
    collectionRef,
    and(
      where("userId", "==", userId),
      where("projectId", "==", projectId),
      where("domainId", "==", domainId),
      where("type", "==", DescriptorType.TAG),
    ),
    limit(1),
  );
  const docSnap = await getDocs(docQuery);

  if (docSnap.empty) {
    throw new NotFoundError(`No tags found for userId: ${userId}, projectId: ${projectId} and domainId: ${domainId}`);
  }

  const tags = {
    userId: docSnap.docs[0].data().userId,
    projectId: docSnap.docs[0].data().projectId,
    id: docSnap.docs[0].data().id,
    type: DescriptorType.TAG,
    domainId: docSnap.docs[0].data().domainId,
    tags: docSnap.docs[0].data().tags || [],
  } as FTags;

  return tags;
};

export const createTag = async (identifier: UniqueIdentifier, tag: Tag, domainId: Domain["id"]): Promise<Tag> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS);

  const docQuery = query(
    collectionRef,
    and(
      where("userId", "==", userId),
      where("projectId", "==", projectId),
      where("domainId", "==", domainId),
      where("type", "==", DescriptorType.TAG),
    ),
    limit(1),
  );
  const docSnap = await getDocs(docQuery);

  const tagsMetadata: Omit<FTags, "tags" | "id"> = {
    userId,
    projectId,
    type: DescriptorType.TAG,
    domainId,
  };

  if (docSnap.empty) {
    const docRef = doc(collectionRef);
    await setDoc(docRef, {
      ...tagsMetadata,
      id: docRef.id,
      tags: arrayUnion(tag),
    });
  } else {
    const existingDoc = docSnap.docs[0];
    await updateDoc(doc(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS, existingDoc.id), {
      ...tagsMetadata,
      id: existingDoc.id,
      tags: arrayUnion(tag),
    });
    return tag;
  }
};

export const updateTag = async (
  identifier: UniqueIdentifier,
  updatedTag: Tag,
  domainId: Domain["id"],
): Promise<Tag> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS);
  const docQuery = query(
    collectionRef,
    and(
      where("userId", "==", userId),
      where("projectId", "==", projectId),
      where("domainId", "==", domainId),
      where("type", "==", DescriptorType.TAG),
    ),
    limit(1),
  );
  const docSnap = await getDocs(docQuery);
  if (docSnap.empty) {
    throw new NotFoundError(`No tags found for userId: ${userId}, projectId: ${projectId} and domainId: ${domainId}`);
  }
  const existingDoc = docSnap.docs[0];
  const existingTags: Tag[] = existingDoc.data().tags || [];

  const doesIdExist = existingTags.some((tag) => tag.id === updatedTag.id);
  if (!doesIdExist) {
    throw new NotFoundError(`Tag with id: ${updatedTag.id} does not exist.`);
  }

  const updatedTags = existingTags.map((tag) => (tag.id === updatedTag.id ? updatedTag : tag));
  await updateDoc(doc(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS, existingDoc.id), {
    tags: updatedTags,
  });
  return updatedTag;
};

export const deleteTag = async (
  identifier: UniqueIdentifier,
  tagId: Tag["id"],
  domainId: Domain["id"],
): Promise<void> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS);

  const docQuery = query(
    collectionRef,
    and(
      where("userId", "==", userId),
      where("projectId", "==", projectId),
      where("domainId", "==", domainId),
      where("type", "==", DescriptorType.TAG),
    ),
    limit(1),
  );
  const docSnap = await getDocs(docQuery);
  if (docSnap.empty) {
    throw new NotFoundError(`No tags found for userId: ${userId}, projectId: ${projectId} and domainId: ${domainId}`);
  }
  const existingDoc = docSnap.docs[0];
  const existingTags: Tag[] = existingDoc.data().tags || [];
  const updatedTags = existingTags.filter((tag) => tag.id !== tagId);
  await updateDoc(doc(fsDb, FIRESTORE_COLLECTION_PATH.DESCRIPTORS, existingDoc.id), {
    tags: updatedTags,
  });
};
